import React from 'react'
import { Config } from '@chegg-wt/core/config'
import dynamic from 'next/dynamic'
import {
  ListItemProps,
  Site,
  ProductConfig,
  PaymentConfig,
  CheckoutCallbackStates,
  ErrorEnum,
  AuthConfig,
  CopyText,
  Config as WTCheckoutConfig,
} from '@chegg/wt-checkout'
import {
  StatusTypes,
  useContentfulOptimizelyExperiment,
} from '@chegg-wt/core/hooks/useContentfulOptimizelyExperiment'
import { Entry } from 'contentful'
import { WT_CHECKOUT_STYLED_CONTAINER } from './testIds'
import * as Styled from './styled'

interface IdToken {
  idToken: string | null
}

interface WtAuthTokens extends IdToken {
  accessToken: string | null
}

interface CheggAuthTokens extends IdToken {
  csrfToken: string | null
}

export type AuthTokens = WtAuthTokens | CheggAuthTokens

const Checkout = dynamic(
  () => import('@chegg/wt-checkout').then((Checkout) => Checkout.Checkout),
  {
    ssr: false,
  }
)
export interface CheckoutWrapperProps {
  site: Site
  featureList: ListItemProps[]
  authTokens: AuthTokens
  productConfig: ProductConfig
  onHostRedirect: () => void
  checkoutCallback: (
    callbackState: CheckoutCallbackStates,
    details: ErrorEnum | Error
  ) => void
  pricingCopy?: CopyText
  paymentConfig?: PaymentConfig
  countryCode?: string
  region?: string
  onClickNoThanks?: () => void
  onClickDismissModal?: () => void
}

interface FeatureListContent {
  slug: string
  items: string[]
}

const entryToUIProps = (entry: Entry<FeatureListContent>): ListItemProps[] =>
  entry.fields.items.map((item) => ({ text: item }))

export const CheckoutWrapper: React.FC<CheckoutWrapperProps> = ({
  site,
  featureList,
  authTokens,
  productConfig,
  onHostRedirect,
  checkoutCallback,
  pricingCopy,
  paymentConfig,
  countryCode,
  region,
  onClickNoThanks,
  onClickDismissModal,
}) => {
  const authConfig: AuthConfig = {
    ...authTokens,
    apiKey: Config.dipApiKey,
    cheggBaseUrl: Config.cheggEnvUrl,
  }

  const defaultPaymentConfig: PaymentConfig = {
    mop: ['creditcard', 'paypal'],
  }

  let config: WTCheckoutConfig = { authConfig, productConfig }

  if (paymentConfig) {
    config = { ...config, paymentConfig }
  } else {
    config = { ...config, paymentConfig: defaultPaymentConfig }
  }

  const token =
    'accessToken' in authTokens ? authTokens.accessToken : authTokens.csrfToken

  const { type, payload } = useContentfulOptimizelyExperiment<FeatureListContent>(
    Config.checkoutFeatureListEntryId
  )

  const uiProps = payload ? entryToUIProps(payload) : featureList

  const checkoutToRender =
    Checkout && authTokens.idToken && token && type === StatusTypes.allocated ? (
      <Styled.CheckoutContainer data-test={WT_CHECKOUT_STYLED_CONTAINER}>
        <Checkout
          featureList={uiProps}
          config={config}
          site={site}
          callback={checkoutCallback}
          modal={false}
          onHostRedirect={onHostRedirect}
          copy={pricingCopy}
          countryCode={countryCode}
          region={region}
          onClickNoThanks={onClickNoThanks}
          onClickDismissModal={onClickDismissModal}
        />
      </Styled.CheckoutContainer>
    ) : null
  return checkoutToRender
}

export default CheckoutWrapper
